import React, { Component } from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { View, StyleSheet, Text } from 'react-native';
import { bookWaitlistSlot, removeWaitlistEntry } from '../actions';
import studioBackground from '../../assets/images/header-images/studio-header.jpg';
import { defaultScale, scaleText, getMomentFormat, getSlotTime } from '../../utils';
import i18n from '../../utils/i18n';
import { BackgroundImageHeader } from '../components/BackgroundImageHeader';
import Theme from '../../theme';
import DurationIcon from '../../assets/images/duration.svg';
import { LocationIcon } from '../../ui/components/Icons';
import { Button, ButtonTypes } from '../../components';
import { getGymTimezone } from '../../utils/gyms';
import { UserProfile, WaitlistEntry } from 'fitbeat.models';
import { GymResolver, WorkoutSessionService } from '../../services';
import { FormError } from '../../ui';
import { waitlistBookErrorSelector, waitlistBookLoadingSelector } from '../selectors';
import { StatusBarComponent } from '../../ui/components/StatusBar';

interface IProps {
  t: any,
  waitlistEntry: any,
  gymTimezone: string,
  userProfile: UserProfile;
  workoutSessionService: any;
  isLoading: boolean;
  error: string | null;
  removeFromWaitlist: (userProfile: UserProfile) => void;
  bookSlot: (workoutSessionService: WorkoutSessionService, userProfile: UserProfile, waitlistEntry: WaitlistEntry) => void;
  closeModal: () => void;
}

class Waitlist extends Component<IProps> {
  render() {
    const {
      t,
      waitlistEntry,
      userProfile,
      isLoading,
      error,
      workoutSessionService,
      closeModal,
      removeFromWaitlist,
      bookSlot,
    } = this.props;
    const date = waitlistEntry.timeSlot.startTime;
    const slot = waitlistEntry.timeSlot;
    const gymTimezone = getGymTimezone(waitlistEntry);
    const formattedTime = getMomentFormat(date, 'HH:mm', gymTimezone)
    const formattedDate = getMomentFormat(date, 'dddd, D MMMM', gymTimezone)
    const workoutTypeDuration = getSlotTime(slot)
    const gym = GymResolver.getByName(waitlistEntry.gymName);
    const isAvailable = !slot.isFull;

    const renderWaitingForSlotActions = () => (
      <Button
        title={t('bookings:removeFromWaitlist')}
        fontColor={Theme.PRIMARY}
        buttonType={ButtonTypes.OUTLINE}
        containerStyle={styles.secondaryAction}
        onPress={() => removeFromWaitlist(userProfile)}
      />
    )

    const renderSlotAvailableActions = () => (
      <>
        <Button
          title={t('bookings:removeFromWaitlist')}
          fontColor={Theme.PRIMARY}
          buttonType={ButtonTypes.OUTLINE}
          containerStyle={styles.primaryAction}
          onPress={!isLoading ? () => removeFromWaitlist(userProfile) : undefined}
        />
        <Button
          title={t('bookings:bookWaitlistSlot')}
          fontColor={Theme.WHITE}
          containerStyle={styles.secondaryAction}
          onPress={() => bookSlot(workoutSessionService, userProfile, waitlistEntry)}
          isLoading={isLoading}
        />
      </>
    )

    return (
      <View style={{ flex: 1, backgroundColor: Theme.GREY_FIGMA }}>
        <StatusBarComponent barStyle='light-content' />
        <BackgroundImageHeader
          imageSource={studioBackground}
          title={isAvailable ? t('waitlistSlotAvailable') : t('onWaitlist')}
          subtitle={isAvailable ? undefined : t('notifyOnceSlotAvailable')}
          closeIconContainerStyle={{
            marginTop: defaultScale(15),
            marginHorizontal: defaultScale(15),
          }}
          gradientHeight='40%'
          useGradient={true}
          containerStyle={{ width: '100%', aspectRatio: 1.3 }}
          contentContainerStyle={{ marginBottom: 0, width: defaultScale(270) }}
          titleStyle={{ marginBottom: 0, paddingVertical: 0 }}
          subTitleStyle={{ marginBottom: 0, marginTop: 0 }}
          onClose={closeModal}
        />

        <View style={styles.content}>
          <View>
            <View style={styles.slot}>
              <Text style={styles.availableSlot}>{isAvailable ? t('availableSlot') : t('waitingForSlot')}:</Text>
              <Text style={styles.slotDate}>{formattedTime}{'\n'}{formattedDate}</Text>
            </View>
            <View style={styles.class}>
              <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'center' }}>
                <View style={styles.durationIcon}>
                  <DurationIcon
                    width={defaultScale(15)}
                    height={defaultScale(15)}
                    color={Theme.BLACK}
                  />
                </View>
                <Text style={styles.duration}>{workoutTypeDuration} {t('class')}</Text>
              </View>
              <View style={styles.divider}></View>
              <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'center' }}>
                <View style={styles.locationIcon}>
                  <LocationIcon
                    size={defaultScale(23)}
                    color={Theme.BLACK}
                  />
                </View>
                <Text style={styles.location}>{gym.displayName}</Text>
              </View>
            </View>
          </View>
          <FormError error={error} />

          <View style={{ paddingHorizontal: defaultScale(15) }}>
            {isAvailable ? renderSlotAvailableActions() : renderWaitingForSlotActions()}
          </View>
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    marginTop: defaultScale(10),
    backgroundColor: Theme.WHITE,
    justifyContent: 'space-between',
  },
  slot: {
    marginTop: defaultScale(40),
  },
  availableSlot: {
    textAlign: 'center',
    ...Theme.textStyle.heading2,
    lineHeight: defaultScale(21.6),
  },
  slotDate: {
    textAlign: 'center',
    ...Theme.textStyle.heading2,
    fontSize: scaleText(21),
    lineHeight: defaultScale(23),
    marginTop: defaultScale(12),
    height: defaultScale(74),
  },
  class: {
    marginTop: defaultScale(12),
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  divider: {
    borderWidth: 0.5,
    borderColor: Theme.GREY_FIGMA_MEDIUM,
    height: defaultScale(32),
    alignItems: 'center',
    justifyContent: 'center',
  },
  durationIcon: {
    justifyContent: 'center',
    marginRight: defaultScale(5),
  },
  locationIcon: {
    justifyContent: 'center',
  },
  duration: {
    ...Theme.textStyle.bodyCopyReg,
    lineHeight: defaultScale(19.2),
  },
  location: {
    ...Theme.textStyle.bodyCopyReg,
    lineHeight: defaultScale(19.2),
    marginLeft: defaultScale(5),
    width: defaultScale(100),
  },
  primaryAction: {
    marginBottom: defaultScale(12),
  },
  secondaryAction: {
    marginBottom: defaultScale(15),
  },
});

const mapStateToProps = (state) => {
  return {
    waitlistEntry: state.bookings.bookingWizard.waitlistEntry,
    isLoading: waitlistBookLoadingSelector(state),
    error: waitlistBookErrorSelector(state),
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    removeFromWaitlist: (userProfile: UserProfile) => dispatch(removeWaitlistEntry(userProfile)),
    bookSlot: (workoutSessionService: WorkoutSessionService, userProfile: UserProfile, waitlistEntry: any) =>
      dispatch(bookWaitlistSlot(workoutSessionService, userProfile, waitlistEntry)),
  };
};

export default translate(['bookings'], { wait: true })(
  connect(mapStateToProps, mapDispatchToProps)(Waitlist));
